import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Contact,
  ContactSpec,
  Folder,
  GalContact,
  Mountpoint,
} from '@zimbra-webif/sdk';
import { useClient } from './ClientContext';

/**
 * An address book as the Contacts screen consumes it. Address books are mail
 * folders whose `view === 'contact'`, including shared ones surfaced as
 * mountpoints (`link[]`). We flatten the tree and tag each with a depth +
 * shared flag, mirroring the Mailbox folder list.
 */
export interface AddressBook {
  id: string;
  name: string;
  depth: number;
  count: number;
  shared: boolean;
  /** owner email for shared books (mountpoints) */
  owner?: string;
}

/**
 * Walk the folder tree (folders + shared `link[]` mountpoints at every level)
 * and keep only the ones with a contact view. Trash/other contact-view system
 * folders are kept as-is; the server returns them with view 'contact' too.
 */
export function buildAddressBooks(root: Folder | undefined): AddressBook[] {
  const books: AddressBook[] = [];
  const walk = (
    folders: Folder[] | undefined,
    links: Mountpoint[] | undefined,
    depth: number,
  ) => {
    for (const f of folders ?? []) {
      if (f.view === 'contact') {
        books.push({
          id: f.id,
          name: f.name ?? '',
          depth,
          count: f.n ?? 0,
          shared: false,
        });
      }
      walk(f.folder, f.link, depth + (f.view === 'contact' ? 1 : 0));
    }
    for (const l of links ?? []) {
      if (l.view === 'contact') {
        const book: AddressBook = {
          id: l.id,
          name: l.name ?? '',
          depth,
          count: l.n ?? 0,
          shared: true,
        };
        if (l.owner) book.owner = l.owner;
        books.push(book);
      }
      walk(l.folder, l.link, depth + (l.view === 'contact' ? 1 : 0));
    }
  };
  walk(root?.folder, root?.link, 0);
  return books;
}

/** Address books (contact-view folders + shared mountpoints) from the folder tree. */
export function useAddressBooks() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['contacts', 'addressbooks'],
    queryFn: async (): Promise<AddressBook[]> => {
      const res = await client.mail.getFolderTree();
      return buildAddressBooks(res.folder?.[0]);
    },
  });
}

/** Contacts in one address book, sorted by file-as name ascending. */
export function useContacts(folderId: string | null) {
  const { client } = useClient();
  return useQuery({
    queryKey: ['contacts', 'list', folderId],
    enabled: folderId !== null,
    queryFn: async (): Promise<Contact[]> => {
      return client.contacts.get({
        folderId: folderId as string,
        sortBy: 'nameAsc',
      });
    },
  });
}

/**
 * GAL (Global Address List) search. Disabled until there's a query term; we
 * surface the directory `entries` (which share the contact `<cn>` wire shape).
 */
export function useGalSearch(term: string) {
  const { client } = useClient();
  const q = term.trim();
  return useQuery({
    queryKey: ['contacts', 'gal', q],
    enabled: q.length >= 2,
    queryFn: async (): Promise<GalContact[]> => {
      const res = await client.gal.search({ name: q, type: 'all', limit: 50 });
      return res.entries;
    },
  });
}

/** Create a personal contact in an address book; refresh that book on success. */
export function useCreateContact() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (spec: ContactSpec) => client.contacts.create(spec, { verbose: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts', 'list'] });
      void qc.invalidateQueries({ queryKey: ['contacts', 'addressbooks'] });
    },
  });
}
